import styled from 'styled-components';
import { Dialog, DialogMessage } from '@elementary/common-ui/components';
import useCustomAlertOverride from 'shared/hooks/useCustomAlertOverride';
import { ReactComponent as DeleteIcon } from '@elementary/common-ui/assets/20x20/trash.svg';
import { space } from '@elementary/common-ui/style/utils';
import { AlertDestination } from '../../../types';

const Bold = styled.span`
  font-size: inherit;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  margin-right: ${space('xs')};
  margin-left: ${space('xs')};
`;

type Props = {
  open: boolean;
  onClose: () => void;
  alertDestination?: AlertDestination;
  latestExecutionTestId?: string;
};

const DeleteAlertOverrideDialog = ({
  open,
  onClose,
  alertDestination,
  latestExecutionTestId,
}: Props) => {
  const {
    customAlertOverride: {
      isDeleteCustomAlertOverrideLoading,
      deleteCustomAlertOverrideMutation,
    },
  } = useCustomAlertOverride({ onDeleteSuccess: onClose });

  const formatDestinationChannel = (channel: string) => {
    if (!alertDestination) {
      return '';
    }
    try {
      const channelObj = JSON.parse(channel);
      if (Array.isArray(channelObj.to)) {
        return channelObj.to.join(', ');
      }
      return alertDestination.channel;
    } catch {
      return `#${alertDestination.channel}`;
    }
  };

  if (!alertDestination) {
    return null;
  }

  return (
    <Dialog
      open={open}
      titleIcon={<DeleteIcon />}
      title="Delete Alert Destination Override"
      message={'Are you sure you want to delete this alert destination?'}
      secondaryMessage={
        <DialogMessage sx={{ color: 'content.tertiary' }}>
          Notifications will no longer be sent to
          <Bold>{formatDestinationChannel(alertDestination.channel)}</Bold>
          as the alert destination.
        </DialogMessage>
      }
      confirmText="Delete"
      cancelText="Cancel"
      onClose={onClose}
      onConfirmButtonClick={() =>
        deleteCustomAlertOverrideMutation({
          destinationId: alertDestination.id,
          entityIds: latestExecutionTestId ? [latestExecutionTestId] : [],
        })
      }
      isLoading={isDeleteCustomAlertOverrideLoading}
    />
  );
};

export default DeleteAlertOverrideDialog;
